import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { reportsAPI } from '../services/api';
import { 
  ChartBarIcon, 
  DocumentArrowDownIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  UsersIcon,
  ArchiveBoxIcon
} from '@heroicons/react/24/outline';
import { LoadingSpinner, Button, Card, Input } from '../components/ui';
import { PageHeader } from '../components/ui';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import toast from 'react-hot-toast';

const COLORS = ['#dc2626', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

/** Respuestas `{ success: true, data: T }` de Axios quedan en `response.data`; extrae `T`. */
function unwrapApiData<T>(response: { data?: unknown } | undefined): T | undefined {
  const body = response?.data;
  if (
    body &&
    typeof body === 'object' &&
    body !== null &&
    'success' in body &&
    (body as { success: boolean }).success === true &&
    'data' in body
  ) {
    return (body as { data: T }).data;
  }
  return body as T | undefined;
}

interface ReportFilters {
  startDate: string;
  endDate: string;
  type: 'payments' | 'clients' | 'plans' | 'service-plans';
  format: 'excel' | 'pdf';
}

const Reports: React.FC = () => {
  const [reportFilters, setReportFilters] = useState<ReportFilters>({
    startDate: '',
    endDate: '',
    type: 'payments',
    format: 'excel'
  });

  const { data: revenueData, isLoading: revenueLoading } = useQuery(
    'revenue',
    () => reportsAPI.getRevenue({ 
      startDate: reportFilters.startDate,
      endDate: reportFilters.endDate 
    })
  );

  const { data: clientData, isLoading: clientLoading } = useQuery(
    'clientReport',
    () => reportsAPI.getClients({ 
      startDate: reportFilters.startDate,
      endDate: reportFilters.endDate 
    })
  );

  const { data: inventoryData, isLoading: inventoryLoading } = useQuery(
    'inventory',
    () => reportsAPI.getInventory()
  );

  const handleGenerateReport = async () => {
    try {
      const response = await reportsAPI.generate(reportFilters);
      const mimeType = reportFilters.format === 'pdf' ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `reporte-${reportFilters.type}-${new Date().toISOString().split('T')[0]}.${reportFilters.format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Reporte generado exitosamente');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Error al generar el reporte');
    }
  };

  const handleFilterChange = (field: keyof ReportFilters, value: string) => {
    setReportFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (revenueLoading || clientLoading || inventoryLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const revenueReport = unwrapApiData<Record<string, unknown>>(revenueData) ?? {};
  const clientReport = unwrapApiData<{
    planStats?: { planType: string; _count?: { id: number } }[];
    totalClients?: number;
    totalWithPayments?: number;
  }>(clientData) ?? {};
  const inventoryReport = unwrapApiData<{
    totalCaskets?: number;
    availableCaskets?: number;
    activePlans?: number;
  }>(inventoryData) ?? {};

  const planChartRows = (clientReport.planStats ?? []).map((p) => ({
    label: p.planType,
    cantidad: p._count?.id ?? 0,
  }));

  return (
    <div className="space-y-8 animate-fade-in-up">
      <PageHeader
        title="Reportes y Análisis"
        subtitle="Análisis de datos operativos y generación de reportes del sistema."
      />

      {/* Report Generator */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Generar Reporte</h3>
              <p className="text-gray-600 text-sm mt-1 hidden sm:block">Configure y genere reportes personalizados</p>
            </div>
            <div className="h-10 w-10 bg-primary-100 rounded-xl flex items-center justify-center border border-primary-200">
              <DocumentArrowDownIcon className="h-5 w-5 text-primary-700" />
            </div>
          </div>
        </div>
        <div className="p-6">
        
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-800 mb-2">
                Tipo de Reporte *
              </label>
              <select
                value={reportFilters.type}
                onChange={(e) => handleFilterChange('type', e.target.value as ReportFilters['type'])}
                title="Seleccionar tipo de reporte"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-white text-gray-900 font-medium"
              >
                <option value="payments">Pagos</option>
                <option value="clients">Clientes</option>
                <option value="plans">Planes por cliente (ataúd y facturas)</option>
                <option value="service-plans">Planes por cliente (resumen)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-800 mb-2">
                Formato *
              </label>
              <select
                value={reportFilters.format}
                onChange={(e) => handleFilterChange('format', e.target.value as ReportFilters['format'])}
                title="Seleccionar formato de reporte"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-white text-gray-900 font-medium"
              >
                <option value="excel">Excel</option>
                <option value="pdf">PDF</option>
              </select>
            </div>

          <div>
            <Input
              label="Fecha Inicio"
              type="date"
              value={reportFilters.startDate}
              onChange={(value) => handleFilterChange('startDate', value)}
            />
          </div>

          <div>
            <Input
              label="Fecha Fin"
              type="date"
              value={reportFilters.endDate}
              onChange={(value) => handleFilterChange('endDate', value)}
            />
          </div>
        </div>

          <div className="mt-8 flex justify-end">
            <Button
              onClick={handleGenerateReport}
              variant="primary"
              size="md"
              className="px-8 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold shadow-lg"
            >
              <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
              Generar Reporte
            </Button>
          </div>
        </div>
      </div>

      {/* Revenue Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-green-50 to-green-100 px-6 py-5 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Análisis de Ingresos</h3>
                <p className="text-gray-600 text-sm mt-1 hidden sm:block">Evolución de los ingresos en el tiempo</p>
              </div>
              <div className="h-10 w-10 bg-green-500 rounded-xl flex items-center justify-center shadow-lg">
                <CurrencyDollarIcon className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
          <div className="p-6">
          
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revenueReport.revenueData || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="period" 
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip 
                  formatter={(value) => [`$${Number(value).toLocaleString()}`, 'Ingresos']}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#dc2626" 
                  strokeWidth={3}
                  dot={{ fill: '#dc2626', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#dc2626', strokeWidth: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          
            <div className="mt-6 grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-2xl font-bold text-green-800">
                  ${revenueReport.totalRevenue?.toLocaleString() || 0}
                </p>
                <p className="text-sm text-green-600 font-medium">Total Ingresos</p>
              </div>
              <div className="text-center p-4 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-2xl font-bold text-green-800">
                  {revenueReport.totalPayments || 0}
                </p>
                <p className="text-sm text-green-600 font-medium">Total Pagos</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-orange-50 to-orange-100 px-6 py-5 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Métodos de Pago</h3>
                <p className="text-gray-600 text-sm mt-1 hidden sm:block">Distribución por método de pago</p>
              </div>
              <div className="h-10 w-10 bg-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                <ChartBarIcon className="h-5 w-5 text-white" />
              </div>
            </div>
          </div>
          <div className="p-6">
          
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={revenueReport.paymentMethodStats || []}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  nameKey="paymentMethod"
                  label={({ name, percent }) => `${String(name ?? '')} ${((percent ?? 0) * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="_sum.amount"
                >
                  {(revenueReport.paymentMethodStats || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `$${Number(value).toLocaleString()}`} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          </div>
        </div>
      </div>

      {/* Client Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Distribución de Planes</h3>
              <p className="text-sm text-gray-500">Clientes por tipo de plantilla (plan)</p>
            </div>
            <div className="h-8 w-8 bg-primary-100 rounded-lg flex items-center justify-center">
              <ChartBarIcon className="h-5 w-5 text-primary-600" />
            </div>
          </div>
          
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={planChartRows}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="label" 
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip />
                <Bar dataKey="cantidad" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Estadísticas de Clientes</h3>
              <p className="text-sm text-gray-500">Resumen de clientes y actividad</p>
            </div>
            <div className="h-8 w-8 bg-primary-100 rounded-lg flex items-center justify-center">
              <UsersIcon className="h-5 w-5 text-primary-700" />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">
                {clientReport.totalClients || 0}
              </p>
              <p className="text-sm text-gray-500">Total Clientes</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">
                {clientReport.totalWithPayments || 0}
              </p>
              <p className="text-sm text-gray-500">Con Pagos</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Inventory Analysis */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Análisis de Inventario</h3>
            <p className="text-sm text-gray-500">Estado actual del inventario</p>
          </div>
          <div className="h-8 w-8 bg-purple-100 rounded-lg flex items-center justify-center">
            <ArchiveBoxIcon className="h-5 w-5 text-purple-600" />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-6 bg-gray-50 rounded-lg">
            <p className="text-3xl font-bold text-gray-900">
              {inventoryReport.totalCaskets ?? 0}
            </p>
            <p className="text-sm text-gray-500">Total Ataúdes</p>
          </div>
          <div className="text-center p-6 bg-gray-50 rounded-lg">
            <p className="text-3xl font-bold text-gray-900">
              {inventoryReport.availableCaskets ?? 0}
            </p>
            <p className="text-sm text-gray-500">Disponibles</p>
          </div>
          <div className="text-center p-6 bg-gray-50 rounded-lg">
            <p className="text-3xl font-bold text-gray-900">
              {inventoryReport.activePlans ?? 0}
            </p>
            <p className="text-sm text-gray-500">Planes Activos</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Reports;
